import React from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const SeoDetailBrandModuleComponent = ({ data }) => {
  const { title, description, footer, cards = [] } = data || {};

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '1010px',
          borderRadius: '48px',
          p: '32px',
          gap: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '3rem',
            lineHeight: '3.25rem',
            letterSpacing: '-0.03em',
            color: '#162F3B',
            textAlign: 'center',
            whiteSpace: 'pre-line',
            '& p': { margin: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: title || '' }}
        />

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1.5rem',
            lineHeight: '1.75rem',
            letterSpacing: '-0.03em',
            color: '#162F3B',
            textAlign: 'center',
            maxWidth: '1013px',
            whiteSpace: 'pre-line',
            '& p': { margin: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: description || '' }}
        />

        {cards.map((card, index) => {
          const linkUrl = card.link?.url || card.ctaUrl || card.href;
          const linkLabel = card.link?.label || card.ctaText || card.linkText;
          const hasCardLink = Boolean(linkUrl && linkLabel);

          return (
            <ScrollFadeWrapper key={index} delay={index * 0.1}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '286px',
                    height: '224px',
                    borderRadius: '34px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: '#A2ACB1',
                  }}
                >
                  {card.image && (
                    <Image
                      width={286}
                      height={224}
                      src={card.image}
                      alt={
                        card.imageAlt || card.title || `Brand card ${index + 1}`
                      }
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: '#FFFFFF',
                    borderRadius: '34px',
                    boxShadow:
                      '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                    boxSizing: 'border-box',
                    p: '32px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '2rem',
                      lineHeight: '2.125rem',
                      color: '#162F3B',
                      textAlign: 'center',
                      mb: '16px',
                      '& p': { margin: 0 },
                    }}
                    dangerouslySetInnerHTML={{ __html: card.title || '' }}
                  />

                  <Box
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      color: '#162F3B',
                      '& p': { m: 0, mb: '1em' },
                      '& p:last-of-type': { mb: 0 },
                      '& strong': { fontWeight: 700 },
                      '& ul': {
                        m: 0,
                        mt: '1em',
                        pl: 0,
                        listStylePosition: 'inside',
                      },
                      '& a': {
                        color: '#C7CB00',
                        textDecoration: 'underline',
                        fontWeight: 700,
                      },
                      '& a:hover': {
                        opacity: 0.85,
                      },
                      '& table': {
                        borderRadius: '24px',
                        overflow: 'hidden',
                        borderCollapse: 'separate !important',
                        borderSpacing: '0 !important',
                      },
                      '& table th, & table td': {
                        borderWidth: '1px !important',
                        borderStyle: 'solid !important',
                        borderColor: '#D6DADB !important',
                      },
                      '& table thead th': {
                        borderTop: '1px solid #D6DADB !important',
                        borderBottom: '1px solid #D6DADB !important',
                      },
                      '& table thead th:not(:first-of-type)': {
                        borderLeft: '0 !important',
                      },
                      '& table thead th:not(:last-of-type)': {
                        borderRight: '0 !important',
                      },
                      '& table th:first-of-type': {
                        borderTopLeftRadius: '24px',
                      },
                      '& table th:last-of-type': {
                        borderTopRightRadius: '24px',
                      },
                      '& table tr:last-of-type td:first-of-type': {
                        borderBottomLeftRadius: '24px',
                      },
                      '& table tr:last-of-type td:last-of-type': {
                        borderBottomRightRadius: '24px',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: card.description || '' }}
                  />

                  {hasCardLink && (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: '16px',
                      }}
                    >
                      <Typography
                        component="a"
                        href={linkUrl}
                        target={card.link?.target || '_self'}
                        rel={
                          (card.link?.target || '_self') === '_blank'
                            ? 'noopener noreferrer'
                            : undefined
                        }
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                          fontSize: '1rem',
                          lineHeight: '1.5rem',
                          letterSpacing: '-0.002em',
                          color: '#C7CB00',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {linkLabel}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </ScrollFadeWrapper>
          );
        })}

        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: '#162F3B',
            maxWidth: '1030px',
          }}
        >
          {footer}
        </Typography>
      </Box>
    </Box>
  );
};

export default SeoDetailBrandModuleComponent;
